import { Dropdown } from "@freightsimple/ui-core/Controls/Dropdown";
import {
  FormItem,
  type ErrorMessageType,
} from "@freightsimple/ui-core/FormLayout/FormItem";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import moment from "moment";
import BookShipmentSmallSection from "../CustomComponents/BookShipmentSmallSection";
import { LocationsDeadlineDateInput } from "@/Shared/Locations/Inputs/LocationsDeadlineDateInput";
import { locationsFirstAllowedDeadlineDate } from "@/Shared/Locations/Helpers/locationsFirstAllowedDeadlineDate";

interface DeadlineSectionProps {
  titleContext: string;
  pickupDate: string;
  earliestDeliveryDate: string | undefined;

  deadlineDate: string | undefined;
  onDeadlineDateChange: (_: moment.Moment | undefined) => void;
  deadlineErrors: ErrorMessageType;

  hasDeadline: boolean | undefined;
  onHasDeadlineChange: (_: boolean) => void;

  /** When true the yes/no is not asked — this warehouse always works to a date. */
  deadlineIsRequired: boolean;

  forceValidation: boolean;
}

export function BookShipmentDeadlinePanel({
  deadlineDate,
  onDeadlineDateChange,
  deadlineErrors,
  pickupDate,
  earliestDeliveryDate,
  titleContext,
  forceValidation,
  hasDeadline,
  onHasDeadlineChange,
  deadlineIsRequired,
}: DeadlineSectionProps) {
  const options = [
    { value: "true", label: "Yes" },
    { value: "false", label: "No" },
  ];
  function handleOptionSelected(value: string) {
    const option = value === "true";
    onHasDeadlineChange(option);

    if (option && !deadlineDate) {
      onDeadlineDateChange(
        locationsFirstAllowedDeadlineDate(pickupDate, earliestDeliveryDate),
      );
    }
  }
  return (
    <BookShipmentSmallSection
      id="delivery-deadline"
      index={0}
      header={<>{titleContext} Deadline</>}
      description={
        deadlineIsRequired
          ? "This warehouse works to a date, so we need the one on your purchase order"
          : "Deadline information helps carriers prioritize your freight accordingly"
      }
    >
      <Panel collapseOnNarrow>
        <Stack gap={10} align="start">
          {!deadlineIsRequired && (
            <Row gap={6}>
              <T size="lg" color="muted">
                Is there a {titleContext.toLowerCase()} deadline?
              </T>
              <Dropdown
                placeholder="Select"
                value={
                  hasDeadline === undefined ? undefined : String(hasDeadline)
                }
                onChange={handleOptionSelected}
                options={options}
                width="md"
                data-testid="has-deadline-dropdown"
              />
            </Row>
          )}

          {hasDeadline && (
            <Row gap={6}>
              <T size="lg" color="muted">
                What is the deadline?
              </T>
              <ShowFormErrors show={forceValidation}>
                <FormItem error={deadlineErrors}>
                  <LocationsDeadlineDateInput
                    pickupDate={pickupDate}
                    earliestDeliveryDate={earliestDeliveryDate}
                    date={
                      deadlineDate === undefined
                        ? undefined
                        : moment(deadlineDate)
                    }
                    onDateChange={onDeadlineDateChange}
                    disabled={hasDeadline !== true}
                  />
                </FormItem>
              </ShowFormErrors>
            </Row>
          )}
        </Stack>
      </Panel>
    </BookShipmentSmallSection>
  );
}
